import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { selectPhoneDetails } from "@/store/selectors/operator.selectors";
import { setPhoneDetails } from "@/store/slices/operator.slice";
import { useRef, useState } from "react";
import { Button } from "../ui/button";
import { validatePhoneNumber } from "@/lib/utils/validators";
import { t } from "i18next";

export function PhoneNumberSelection() {
  const dispatch = useAppDispatch();
  const phoneDetails = useAppSelector(selectPhoneDetails);
  const componentRef = useRef<HTMLDivElement>(null);
  const [hasConfirmed, setHasConfirmed] = useState(false);

  const scrollToBottom = () => {
    // Using requestAnimationFrame to ensure DOM updates are complete
    requestAnimationFrame(() => {
      window.scrollTo({
        top: window.scrollY + 300,
        behavior: "smooth",
      });
    });
  };

  const handleNumberTypeChange = (value: "new" | "port") => {
    dispatch(
      setPhoneDetails({ numberType: value, phoneNumber: null, error: "" })
    );
    setHasConfirmed(false);
  };

  const handlePhoneNumberChange = (value: string) => {
    dispatch(
      setPhoneDetails({
        numberType: phoneDetails.numberType,
        phoneNumber: value,
        error: "",
      })
    );
    setHasConfirmed(false);
  };

  const handleOnBlur = (value: string) => {
    if (!phoneDetails.phoneNumber) {
      dispatch(
        setPhoneDetails({
          numberType: phoneDetails.numberType,
          phoneNumber: phoneDetails.phoneNumber,
          error: t("operators.phoneRequired"),
        })
      );
    }

    if (value && !validatePhoneNumber(value)) {
      dispatch(
        setPhoneDetails({
          numberType: phoneDetails.numberType,
          phoneNumber: value,
          error: t("operators.invalidPhone"),
        })
      );
    }
  };

  const canContinue = () => {
    if (phoneDetails.numberType === "new") return true;
    if (phoneDetails.numberType === "port") {
      return (
        phoneDetails.phoneNumber &&
        validatePhoneNumber(phoneDetails.phoneNumber)
      );
    }
    return false;
  };

  const handleContinue = () => {
    if (
      phoneDetails.numberType === "port" &&
      phoneDetails.phoneNumber &&
      !validatePhoneNumber(phoneDetails.phoneNumber)
    ) {
      dispatch(
        setPhoneDetails({
          ...phoneDetails,
          error: t("operators.invalidPhone"),
        })
      );
      return;
    }

    setHasConfirmed(true);

    scrollToBottom();
  };

  return (
    <div ref={componentRef} className="mt-4 w-full min-w-0 space-y-4">
      <RadioGroup
        defaultValue=""
        value={phoneDetails.numberType || ""}
        onValueChange={(value: "new" | "port") => handleNumberTypeChange(value)}
        className="flex flex-col space-y-1"
      >
        <div className="flex items-center space-x-2">
          <RadioGroupItem value="new" id="new" />
          <Label htmlFor="new">{t("operators.newPhoneNumber")}</Label>
        </div>
        <div className="flex items-center space-x-2">
          <RadioGroupItem value="port" id="port" />
          <Label htmlFor="port">{t("operators.portPhoneNumber")}</Label>
        </div>
      </RadioGroup>

      {phoneDetails.numberType === "port" && (
        <div className="w-full min-w-0 space-y-2 overflow-visible">
          <Input
            placeholder={t("operators.portNumberPlaceholder")}
            value={phoneDetails.phoneNumber || ""}
            onChange={(e) => handlePhoneNumberChange(e.target.value)}
            onBlur={(e) => handleOnBlur(e.target.value)}
            className={`w-full max-w-full box-border focus-visible:ring-inset focus-visible:ring-offset-0 ${
              phoneDetails.error ? "border-red-500" : ""
            }`}
          />
          {phoneDetails.error && (
            <p className="text-sm text-red-500">{phoneDetails.error}</p>
          )}
        </div>
      )}

      {phoneDetails.numberType && !hasConfirmed && (
        <Button
          variant="default"
          className="mt-4"
          onClick={handleContinue}
          disabled={!canContinue()}
        >
          {t("operators.continue")}
        </Button>
      )}
    </div>
  );
}
